/**
 * IP Blocking Service
 * Now uses Firebase for persistence with in-memory cache for speed
 */

import FirebaseSecurityService from './firebaseSecurityService';

export interface BlockedIP {
    id: string;
    ip: string;
    reason: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    blockedAt: Date;
    blockedBy: string;
    isActive: boolean;
    expiresAt?: Date;
}

export interface SuspiciousActivity {
    id: string;
    ip: string;
    type: string;
    details: string;
    timestamp: Date;
    severity: 'low' | 'medium' | 'high' | 'critical';
}

export interface BlockingStats {
    totalBlocked: number;
    activeBlocks: number;
    suspiciousActivities: number;
    autoBlocked: number;
}

class IPBlockingService {
    private static suspiciousActivities: SuspiciousActivity[] = [];
    private static isInitialized = false;

    /**
     * Initialize service
     */
    static async initialize(): Promise<void> {
        if (this.isInitialized) return;
        
        try {
            await FirebaseSecurityService.initialize();
            this.isInitialized = true;
            console.log('✅ IP Blocking Service initialized with Firebase');
        } catch (error) {
            console.error('❌ Failed to initialize IP Blocking Service:', error);
        }
    }

    /**
     * Block an IP address (saves to Firebase)
     */
    static async blockIP(
        ip: string,
        reason: string,
        severity: 'low' | 'medium' | 'high' | 'critical',
        blockedBy: string,
        expiresInDays?: number
    ): Promise<{ success: boolean; message: string }> {
        await this.ensureInitialized();
        return await FirebaseSecurityService.blockIP(ip, reason, severity, blockedBy, expiresInDays);
    }

    /**
     * Unblock an IP address (updates Firebase)
     */
    static async unblockIP(ip: string, unblockedBy: string = 'admin'): Promise<{ success: boolean; message: string }> {
        await this.ensureInitialized();
        return await FirebaseSecurityService.unblockIP(ip, unblockedBy);
    }

    /**
     * Check if an IP is blocked (fast - from cache)
     */
    static async isIPBlocked(ip: string): Promise<boolean> {
        await this.ensureInitialized();
        return FirebaseSecurityService.isIPBlocked(ip);
    }

    /**
     * Get all blocked IPs (from cache)
     */
    static async getBlockedIPs(): Promise<BlockedIP[]> {
        await this.ensureInitialized();
        return FirebaseSecurityService.getBlockedIPs();
    }

    /**
     * Add suspicious activity
     */
    static async addSuspiciousActivity(
        ip: string,
        type: string,
        details: string,
        severity: 'low' | 'medium' | 'high' | 'critical'
    ): Promise<SuspiciousActivity> {
        const suspicious: SuspiciousActivity = {
            id: Date.now().toString(),
            ip,
            type,
            details,
            timestamp: new Date(),
            severity
        };

        this.suspiciousActivities.push(suspicious);
        console.log('⚠️ Suspicious activity detected:', ip, type, severity);
        return suspicious;
    }

    /**
     * Get suspicious activities
     */
    static async getSuspiciousActivities(): Promise<SuspiciousActivity[]> {
        return [...this.suspiciousActivities];
    }

    /**
     * Get blocking statistics
     */
    static async getBlockingStats(): Promise<BlockingStats> {
        await this.ensureInitialized();
        const blockedIPs = FirebaseSecurityService.getBlockedIPs();
        
        return {
            totalBlocked: blockedIPs.length,
            activeBlocks: blockedIPs.filter(ip => ip.isActive).length,
            suspiciousActivities: this.suspiciousActivities.length,
            autoBlocked: blockedIPs.filter(ip => ip.blockedBy === 'system').length
        };
    }

    /**
     * Ensure service is initialized
     */
    private static async ensureInitialized(): Promise<void> {
        if (!this.isInitialized) {
            await this.initialize();
        }
    }
}

export default IPBlockingService;
